<template>
	<div class="forum-message-thread">
		<article class="forum-message" :class="{ 'is-reply': isChild }">
			<div class="forum-message-avatar" :style="avatarStyle" :title="authorEmail">
				{{ avatarInitials }}
			</div>
			<div class="forum-message-body">
				<header class="forum-message-header">
					<span class="forum-message-author" :title="authorEmail">{{ authorName }}</span>
					<span v-if="authorHandle" class="forum-message-handle">@{{ authorHandle }}</span>
					<span class="forum-message-time" :title="comment.createdAt">{{ formatTime(comment.createdAt) }}</span>
					<span v-if="isEdited" class="forum-message-edited"
						:title="`Edited ${comment.updatedAt}`">(edited)</span>
				</header>

				<!-- Display mode -->
				<template v-if="!isEditing">
					<div class="forum-message-content rich-content" v-html="renderedBody"></div>

					<div class="forum-message-actions">
						<button type="button" class="forum-msg-action forum-msg-action-react" :class="{
							'has-reacted': comment.hasReacted,
							'reaction-bumping': isReactionBumping,
							'forum-msg-action-locked': isLikeLocked,
						}" :disabled="isLikeLocked"
							:title="reactButtonTitle"
							@click="onReact">
							<i :class="comment.hasReacted ? 'fas fa-thumbs-up' : 'far fa-thumbs-up'"></i>
							<span>{{ comment.hasReacted ? "Liked" : "Like" }}</span>
							<span v-if="(comment.reactionCount || 0) > 0" class="forum-msg-count">{{ comment.reactionCount }}</span>
						</button>

						<button v-if="canShowReplyButton" type="button" class="forum-msg-action" @click="onReplyClick">
							Reply
						</button>

						<button v-if="canEdit" type="button" class="forum-msg-action" @click="startEdit" title="Edit">
							Edit
						</button>

						<button v-if="canDelete" type="button" class="forum-msg-action forum-msg-action-danger"
							@click="onDeleteClick" title="Delete">
							Delete
						</button>
					</div>
				</template>

				<div v-else class="forum-edit-composer">
					<MarkdownEditor ref="editEditor"
					 :key="editKey"
					 :autofocus="true"
					 :compact="true"
					 :show-actions="false"
					 :allow-mentions="mentionsEnabled"
					 :search-users="debouncedMentionSearch"
					 :upload-entity="entityType.Comment"
					 :allow-attachments="commentAttachmentPolicy.isAllowed"
					 :allowed-attachment-types="commentAttachmentPolicy.allowedTypes"
					 placeholder="Edit your comment…"
					 send-label="Save"
					 @update="onEditUpdate" />

					<div class="forum-edit-bar">
						<button type="button" class="forum-msg-action" @click="cancelEdit" :disabled="isSavingEdit">Cancel</button>
						<button type="button" class="forum-reply-send" :disabled="!canSaveEdit || isSavingEdit" @click="saveEdit">{{ isSavingEdit ? "Saving…" : "Save" }}</button>
					</div>
				</div>
			</div>
		</article>

		<div v-if="showRepliesSection" class="forum-replies">
			<CommentItem v-for="child in (comment.children || [])" :key="child.id" :comment="child"
				:root-entity-id="rootEntityId" :root-entity-type="rootEntityType"
				:product-variant-id="productVariantId" :depth="depth + 1" :is-comments-locked="isCommentsLocked"
				:is-reactions-locked="isReactionsLocked" :is-viewer-moderator="isViewerModerator"
				:is-writable="isWritable"
				@react="(reactedComment) => $emit('react', reactedComment)"
				@delete="(deletedComment) => $emit('delete', deletedComment)"
				@bubble-up-reply="(replyPayload) => $emit('bubble-up-reply', replyPayload)"
				@reply="(replyPayload) => $emit('reply', replyPayload)" />

			<button v-if="hasMoreRepliesToLoad" type="button" class="forum-show-more-replies" :disabled="isLoadingMoreReplies"
				@click="loadMoreReplies">
				<i :class="isLoadingMoreReplies ? 'fas fa-circle-notch fa-spin' : 'fas fa-chevron-down'"></i>
				{{ loadMoreLabel }}
			</button>

			<div v-if="isReplyBoxOpen && canShowReplyButton" class="forum-reply-composer">
				<MarkdownEditor ref="replyEditor" :key="replyKey" :autofocus="true" :compact="true" :show-actions="false"
					:allow-mentions="mentionsEnabled"
					:search-users="debouncedMentionSearch" :upload-entity="entityType.Comment"
					:allow-attachments="commentAttachmentPolicy.isAllowed"
					:allowed-attachment-types="commentAttachmentPolicy.allowedTypes"
					placeholder="Write a reply… drop files to attach" send-label="Reply"
					@update="onReplyEditorUpdate" />
				<div class="forum-reply-bar">
					<button type="button" class="forum-msg-action" @click="cancelReply">Cancel</button>
					<button type="button" class="forum-reply-send" :disabled="!canSubmitReply" @click="submitReply">
						Reply
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import MarkdownEditor from "./Editor/MarkdownEditor.vue";
import { AVATAR_COLORS, MENTIONS_ENABLED, MAX_REPLY_DEPTH } from "../../constants/forum";
import { EntityType } from "../../constants/entity";
import { resolveAttachmentPolicy } from "../../utils/attachment-utility";
import { markdownToHTML } from "../../utils/markdown";
import { registerAttachments } from "../../utils/attachment-registry";
import { createMentionSearch } from "../../utils/mention-search";

export default {
	name: "CommentItem",
	components: { MarkdownEditor },
	props: {
		comment: { type: Object, required: true },
		// Non-comment root the comment tree hangs off of. Forwarded to
		// interactions store actions for the bucket lookup.
		rootEntityId: { type: [Number, String], required: true },
		rootEntityType: { type: String, required: true },
		depth: { type: Number, default: 0 },
		productVariantId: { type: [Number, String], default: null },
		isCommentsLocked: { type: Boolean, default: false },
		isReactionsLocked: { type: Boolean, default: false },
		isViewerModerator: { type: Boolean, default: false },
		// Root-container write gate. When false, all writes are blocked
		// even if the per-entity locks above are open. Reads pass through.
		isWritable: { type: Boolean, default: true },
	},
	data() {
		return {
			isReplyBoxOpen: false,
			replyPayload: null,
			replyKey: Date.now(),
			isReactionBumping: false,
			isEditing: false,
			editPayload: null,
			editKey: 0,
			isSavingEdit: false,
			isLoadingMoreReplies: false,
			entityType: EntityType,
			mentionsEnabled: MENTIONS_ENABLED,
			debouncedMentionSearch: createMentionSearch({
				productVariantId: this.productVariantId,
			}),
		};
	},
	computed: {
		...mapState("user", ["user"]),
		...mapState("appState", ["systemParameters"]),
		commentAttachmentPolicy() {
			return resolveAttachmentPolicy(this.systemParameters, EntityType.Comment);
		},
		isChild() { return this.depth > 0; },
		canReplyInline() { return this.depth < MAX_REPLY_DEPTH; },
		author() {
			return this.comment.author || null;
		},
		authorName() {
			const author = this.author;
			if (!author) return "Anonymous";
			const firstName = (author.firstName || "").trim();
			const lastName = (author.lastName || "").trim();
			if (firstName || lastName) return `${firstName} ${lastName}`.trim();
			if (author.username) return author.username;
			return "Anonymous";
		},
		authorHandle() {
			if (!this.author) return "";
			const hasFull = (this.author.firstName || "").trim() || (this.author.lastName || "").trim();
			return hasFull && this.author.username ? this.author.username : "";
		},
		authorEmail() {
			return this.author?.email || "";
		},
		avatarInitials() {
			const nameWords = this.authorName.trim().split(/\s+/);
			if (nameWords.length >= 2) return (nameWords[0][0] + nameWords[1][0]).toUpperCase();
			return (nameWords[0]?.[0] || "?").toUpperCase();
		},
		avatarStyle() {
			const colorIndex = ((this.comment.createdBy || 0) + (this.avatarInitials.charCodeAt(0) || 0)) % AVATAR_COLORS.length;
			return { backgroundColor: AVATAR_COLORS[colorIndex] };
		},
		// Moderator delete bypasses the container freeze (cleanup carve-out);
		// author delete respects it.
		canDelete() {
			if (!this.user) return false;
			if (this.isViewerModerator) return true;
			if (this.isSameId(this.comment.createdBy, this.user.id)) return this.isWritable;
			return false;
		},
		canEdit() {
			if (!this.user) return false;
			if (!this.isWritable) return false;
			return this.isSameId(this.comment.createdBy, this.user.id);
		},
		isLikeLocked() {
			return !this.isWritable || this.isReactionsLocked;
		},
		reactButtonTitle() {
			if (!this.isWritable) return "New likes are paused right now.";
			if (this.isReactionsLocked) return "Reactions are locked on this thread.";
			return this.comment.hasReacted ? "Unlike" : "Like";
		},
		canShowReplyButton() {
			return !this.isCommentsLocked && this.isWritable;
		},
		canSaveEdit() {
			if (!this.editPayload) return false;
			const plainText = (this.editPayload.text || "").trim();
			const markdown = (this.editPayload.markdown || "").trim();
			return plainText.length > 0 || markdown.length > 0;
		},
		isEdited() {
			if (!this.comment.updatedAt || !this.comment.createdAt) return false;
			return new Date(this.comment.updatedAt).getTime()
				> new Date(this.comment.createdAt).getTime() + 1000;
		},
		renderedBody() {
			registerAttachments(this.comment.attachments || []);
			return markdownToHTML(this.comment.body || "");
		},
		canSubmitReply() {
			if (!this.replyPayload) return false;
			const plainText = (this.replyPayload.text || "").trim();
			const markdown = (this.replyPayload.markdown || "").trim();
			return plainText.length > 0 || markdown.length > 0;
		},
		// childCount drives the button label only, not visibility — see
		// hasMoreRepliesToLoad.
		remainingReplies() {
			const total = this.comment.childCount || 0;
			const loaded = (this.comment.children || []).length;
			return Math.max(0, total - loaded);
		},
		hasLoadedReplies() {
			return !!(this.comment.children && this.comment.children.length);
		},
		// Pre-fetch: trust childCount (only signal we have). Post-fetch:
		// trust the server cursor — childCount can drift but the cursor is
		// authoritative. Gating on `childrenPageFetched` (not
		// `hasLoadedReplies`) so a locally-posted reply doesn't flip us
		// into post-fetch mode prematurely.
		hasMoreRepliesToLoad() {
			if (!this.comment.childrenPageFetched) return this.remainingReplies > 0;
			return !!this.comment.childrenNextPageToken;
		},
		showRepliesSection() {
			return this.hasLoadedReplies || this.hasMoreRepliesToLoad || this.isReplyBoxOpen;
		},
		// Generic "Load more replies" when the math says 0 but the cursor
		// says more exists — avoids "Load 0 more replies".
		loadMoreLabel() {
			if (this.isLoadingMoreReplies) return "Loading…";
			const remaining = this.remainingReplies;
			const loadedCount = (this.comment.children || []).length;
			if (loadedCount === 0) {
				const replyNoun = remaining === 1 ? "reply" : "replies";
				return `View ${remaining} ${replyNoun}`;
			}
			if (remaining > 0) {
				const replyNoun = remaining === 1 ? "reply" : "replies";
				return `Load ${remaining} more ${replyNoun}`;
			}
			return "Load more replies";
		},
	},
	methods: {
		...mapActions("interactions", ["updateComment"]),
		// auth-response user.id is a string, comment.createdBy is a number
		// — coerce both via Number so strict === doesn't hide Edit/Delete.
		isSameId(a, b) {
			if (a == null || b == null) return false;
			const numA = Number(a);
			const numB = Number(b);
			if (Number.isNaN(numA) || Number.isNaN(numB)) return false;
			return numA === numB;
		},
		showError(message, options = {}) {
			this.$toasted.error(message, { icon: "exclamation-triangle", theme: "outline", position: "bottom-center", singleton: true, duration: 3000, ...options });
		},
		showSuccess(message, options = {}) {
			this.$toasted.success(message, { icon: "check", theme: "outline", position: "bottom-center", duration: 3000, ...options });
		},
		startEdit() {
			this.isEditing = true;
			this.editPayload = null;
			this.editKey = Date.now();
			// Wait for the editor to mount, then seed the doc — setMarkdown
			// registers attachment metadata so existing [[file:id]] tokens
			// resolve to real file cards.
			this.$nextTick(() => {
				const editor = this.$refs.editEditor;
				if (!editor) return;
				if (typeof editor.setMarkdown === "function") {
					editor.setMarkdown(this.comment.body || "", this.comment.attachments || []);
				}
			});
		},
		cancelEdit() {
			this.isEditing = false;
			this.editPayload = null;
		},
		onEditUpdate(payload) {
			this.editPayload = payload;
		},
		async saveEdit() {
			if (!this.canSaveEdit || this.isSavingEdit) return;

			const editor = this.$refs.editEditor;
			const payload = editor && editor.serialize ? editor.serialize() : this.editPayload;

			// Final attachment IDs — backend diffs against the comment's
			// current set, claiming new drafts and soft-deleting removed ones
			// atomically.
			const attachmentIds = ((payload && payload.attachments) || [])
				.filter((attachment) => !attachment.uploading)
				.map((attachment) => attachment.id)
				.filter((attachmentId) => attachmentId != null);

			this.isSavingEdit = true;
			try {
				await this.updateComment({
					id: this.comment.id,
					body: (payload && payload.markdown) || "",
					attachmentIds,
					rootEntityType: this.rootEntityType,
					rootEntityId: this.rootEntityId,
				});
				this.isEditing = false;
				this.editPayload = null;
			} catch (saveError) {
				this.showError(saveError?.message || "Failed to save.");
			} finally {
				this.isSavingEdit = false;
			}
		},
		onReact() {
			// :disabled also guards this, but a stale cached click could
			// still fire — bail to avoid a flicker.
			if (this.isReactionsLocked) return;
			// Animate only on the like-on transition.
			if (!this.comment.hasReacted) {
				this.isReactionBumping = true;
				setTimeout(() => { this.isReactionBumping = false; }, 600);
			}
			this.$emit("react", this.comment);
		},
		onReplyClick() {
			if (this.isCommentsLocked) return;
			// At max depth (2), the Reply button bubbles up to the thread
			// composer with the author pre-mentioned, continuing as a new
			// top-level comment.
			if (this.canReplyInline) {
				this.isReplyBoxOpen = !this.isReplyBoxOpen;
			} else {
				this.$emit("bubble-up-reply", { author: this.author });
			}
		},
		async onDeleteClick() {
			const confirmed = await this.$bvModal.msgBoxConfirm(
				"Are you sure you want to delete this comment? Replies under it will also be removed.",
				{
					title: "Delete comment?",
					okTitle: "Delete",
					okVariant: "danger",
					cancelTitle: "Cancel",
					centered: true,
				},
			);
			if (confirmed) this.$emit("delete", this.comment);
		},
		// Cursor lives on this node from the previous page. Merge dedupes
		// and re-sorts oldest-first so a reply posted by another user
		// during pagination slots in chronologically.
		async loadMoreReplies() {
			if (this.isLoadingMoreReplies || !this.hasMoreRepliesToLoad) return;
			this.isLoadingMoreReplies = true;
			try {
				await this.$store.dispatch("interactions/fetchMoreReplies", {
					rootEntityType: this.rootEntityType,
					rootEntityId: this.rootEntityId,
					parentId: this.comment.id,
					nextPageToken: this.comment.childrenNextPageToken || null,
				});
			} catch (loadError) {
				this.showError(loadError?.message || "Failed to load more replies.");
			} finally {
				this.isLoadingMoreReplies = false;
			}
		},
		onReplyEditorUpdate(payload) {
			this.replyPayload = payload;
		},
		submitReply() {
			if (!this.canSubmitReply) return;

			const payload = this.$refs.replyEditor && this.$refs.replyEditor.serialize
				? this.$refs.replyEditor.serialize()
				: this.replyPayload;

			const attachmentIds = ((payload && payload.attachments) || [])
				.filter((attachment) => !attachment.uploading)
				.map((attachment) => attachment.id)
				.filter((attachmentId) => attachmentId != null);

			this.$emit("reply", {
				parentComment: this.comment,
				body: (payload && payload.markdown) || "",
				attachmentIds,
			});
			this.replyPayload = null;
			this.isReplyBoxOpen = false;
			this.replyKey = Date.now();
		},
		cancelReply() {
			this.isReplyBoxOpen = false;
			this.replyPayload = null;
			this.replyKey = Date.now();
		},
		formatTime(iso) {
			if (!iso) return "";
			const timestamp = new Date(iso);
			const diffMs = Date.now() - timestamp.getTime();
			const diffMinutes = Math.floor(diffMs / 60000);
			if (diffMinutes < 1) return "just now";
			if (diffMinutes < 60) return `${diffMinutes}m`;
			const diffHours = Math.floor(diffMinutes / 60);
			if (diffHours < 24) return `${diffHours}h`;
			const diffDays = Math.floor(diffHours / 24);
			if (diffDays < 7) return `${diffDays}d`;
			return timestamp.toLocaleDateString();
		},
	},
	watch: {
		// Mirrors NewThreadModal — rebuild the debouncer if scope changes mid-life
		// (parent thread switching context). Same key forwarded to nested replies via prop.
		productVariantId(next) {
			this.debouncedMentionSearch = createMentionSearch({ productVariantId: next });
		},
	},
};
</script>

<style scoped>
/* Each thread is one comment + (optionally) its replies block. A subtle
   bottom rule separates threads so the list reads as a flat conversation
   rather than a stack of cards. */
.forum-message-thread {
	padding: 10px 0;
	border-bottom: 1px solid #f0f0f0;
}

.forum-message-thread:last-child {
	border-bottom: none;
}

.forum-message {
	display: flex;
	gap: 10px;
	align-items: flex-start;
}

.forum-message.is-reply {
	margin-top: 6px;
}

.forum-message-avatar {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	color: #ffffff;
	font-weight: 600;
	font-size: 13px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	letter-spacing: -0.02em;
	user-select: none;
}

.forum-message.is-reply .forum-message-avatar {
	width: 28px;
	height: 28px;
	font-size: 11px;
}

.forum-message-body {
	flex: 1;
	min-width: 0;
}

.forum-message-header {
	display: flex;
	align-items: baseline;
	gap: 8px;
	margin-bottom: 2px;
	flex-wrap: wrap;
}

.forum-message-author {
	font-weight: 600;
	color: #242424;
	font-size: 14px;
}

.forum-message.is-reply .forum-message-author {
	font-size: 13px;
}

.forum-message-handle {
	font-size: 11.5px;
	color: #616161;
	font-weight: 400;
}

.forum-message-time {
	font-size: 11px;
	color: #909090;
}

.forum-message-edited {
	font-size: 11px;
	font-style: italic;
	color: #909090;
}

/* Flat content area — no bubble. */
.forum-message-content {
	color: #242424;
	font-size: 13.5px;
	line-height: 1.5;
	word-break: break-word;
}

.forum-message.is-reply .forum-message-content {
	font-size: 13px;
}

.forum-message-content.rich-content p {
	margin: 4px 0;
}

.forum-message-content.rich-content p:first-child {
	margin-top: 0;
}

.forum-message-content.rich-content p:last-child {
	margin-bottom: 0;
}

.forum-message-content.rich-content ul,
.forum-message-content.rich-content ol {
	padding-left: 22px;
	margin: 4px 0;
}

.forum-message-content.rich-content blockquote {
	border-left: 3px solid var(--secondary-color, #03324d);
	padding: 2px 10px;
	margin: 6px 0;
	color: #616161;
	background: rgba(3, 50, 77, 0.06);
	border-radius: 0 4px 4px 0;
}

.forum-message-content.rich-content code {
	background: rgba(0, 0, 0, 0.06);
	padding: 1px 5px;
	border-radius: 4px;
	font-family: Consolas, Menlo, Monaco, monospace;
	font-size: 0.92em;
}

.forum-message-content.rich-content pre {
	background: #1f1f1f;
	color: #e1e1e1;
	padding: 10px 12px;
	border-radius: 6px;
	font-size: 12.5px;
	overflow-x: auto;
	margin: 6px 0;
	font-family: Consolas, Menlo, Monaco, monospace;
}

.forum-message-content.rich-content pre code {
	background: transparent;
	padding: 0;
	color: inherit;
}

.forum-message-content.rich-content a {
	color: var(--secondary-color, #03324d);
	text-decoration: underline;
}

.forum-message-content.rich-content img {
	max-width: 100%;
	border-radius: 6px;
}

/* Mention pill — high-specificity match on the .mention <span> the
   markdown-it rule emits. Same look as the editor's inline pills. */
.forum-message-content.rich-content .mention {
	background: rgba(3, 50, 77, 0.1);
	color: var(--secondary-color, #03324d);
	padding: 1px 6px;
	border-radius: 4px;
	font-weight: 500;
	white-space: nowrap;
	cursor: default;
}


/* Inline text-style action buttons (Reply / React / Delete pattern) —
   the reference doesn't put them in pills, just spaced text links. */
.forum-message-actions {
	display: flex;
	align-items: center;
	gap: 14px;
	margin-top: 6px;
	font-size: 12px;
	color: #616161;
}

.forum-msg-action {
	background: none;
	border: none;
	padding: 2px 0;
	color: var(--secondary-color, #03324d);
	font-weight: 500;
	font-size: 12px;
	cursor: pointer;
	font-family: inherit;
	display: inline-flex;
	align-items: center;
	gap: 5px;
}

.forum-msg-action:hover {
	text-decoration: underline;
}

.forum-msg-action i {
	font-size: 12px;
}

.forum-msg-action.forum-msg-action-react.has-reacted {
	color: var(--secondary-color, #03324d);
	font-weight: 600;
}

.forum-msg-action.forum-msg-action-react.has-reacted i {
	color: var(--primary-color, #ffd83f);
}

/* Disabled-but-styled state for locked reactions / locked comments. The
   button still shows its current value (e.g. "Liked" + count stays
   highlighted if the user previously liked) — only the click affordance
   is removed. Crucially we do NOT desaturate the "is-liked" colors so
   "you liked this" remains visually true. */
.forum-msg-action.forum-msg-action-locked,
.forum-msg-action[disabled] {
	cursor: not-allowed;
	opacity: 0.7;
}

.forum-msg-action.forum-msg-action-locked:hover,
.forum-msg-action[disabled]:hover {
	text-decoration: none;
}

/* Override opacity on the previously-liked state so the highlight
   stays at full strength — that's the whole point of issue 3. */
.forum-msg-action.forum-msg-action-react.has-reacted.forum-msg-action-locked,
.forum-msg-action.forum-msg-action-react.has-reacted[disabled] {
	opacity: 1;
}

/* Like-button pop animation. Triggers when .reaction-bumping is set briefly
   on the click that turns liked-on — so already-liked comments don't
   pop on every page render. The cubic-bezier overshoots slightly for
   that satisfying "bounce" feel familiar from FB / YouTube. */
.forum-msg-action-react i {
	display: inline-block;
	transition: color 0.18s ease, transform 0.18s ease;
}

.forum-msg-action-react.reaction-bumping i {
	animation: forum-reaction-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1);
	color: var(--primary-color, #ffd83f);
}

@keyframes forum-reaction-pop {
	0% {
		transform: scale(1);
	}

	18% {
		transform: scale(0.82);
	}

	55% {
		transform: scale(1.42);
	}

	100% {
		transform: scale(1);
	}
}

@media (prefers-reduced-motion: reduce) {
	.forum-msg-action-react.reaction-bumping i {
		animation: none;
	}
}

.forum-msg-count {
	font-variant-numeric: tabular-nums;
	font-weight: 600;
	color: inherit;
	padding-left: 1px;
}

/* Delete sits inline with Edit using the same `.forum-message-actions`
   gap — no `margin-left: auto` push, so the affordance order in the
   row is Like, Reply, Edit, Delete with uniform spacing. Danger color
   from the start (not the previous grey-until-hover) so it reads
   correctly without requiring hover discovery; underline on hover is
   inherited from the base `.forum-msg-action:hover` rule. */
.forum-msg-action.forum-msg-action-danger {
	color: var(--color-danger, #d3131a);
}

.forum-msg-action.forum-msg-action-danger:hover {
	color: var(--color-danger, #d3131a);
	text-decoration: underline;
}

/* Replies block — sibling of the parent message, indented with a left
   rule. The rule also visually anchors the reply composer at the bottom. */
.forum-replies {
	margin-left: 46px;
	padding-left: 12px;
	border-left: 2px solid rgba(3, 50, 77, 0.12);
	margin-top: 4px;
}

.forum-reply-composer {
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 8px 0;
}

.forum-reply-bar {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
}

.forum-reply-send {
	background: var(--secondary-color, #03324d);
	color: #ffffff;
	border: none;
	padding: 6px 16px;
	border-radius: 999px;
	font-size: 12.5px;
	font-weight: 500;
	cursor: pointer;
	transition: background-color 0.15s ease;
	font-family: inherit;
}

.forum-reply-send:hover:not(:disabled) {
	background: var(--secondary-color-hover, #032a41);
}

.forum-reply-send:disabled {
	background: #d8d8d8;
	cursor: not-allowed;
}

/* In-place edit composer mirrors the reply composer's slim-stack look. */
.forum-edit-composer {
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 4px 0 0;
}

/* "Show N more replies" — text-link style at the end of the replies block,
   indented to align with the reply messages above it. */
.forum-show-more-replies {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: none;
	border: none;
	padding: 6px 0;
	color: var(--secondary-color, #03324d);
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	font-family: inherit;
}

.forum-show-more-replies:hover {
	text-decoration: underline;
}

.forum-show-more-replies i {
	font-size: 10px;
}

.forum-edit-bar {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
}

@media (max-width: 600px) {

	/* Indent shrinks so a depth-2 reply (the deepest the backend allows)
	   still has room to render the avatar + body without overlap on a
	   375px-wide phone. 18px + 8px padding = ~26px per level; max 52px
	   total indent for depth 2. */
	.forum-replies {
		margin-left: 18px;
		padding-left: 8px;
	}

	.forum-message {
		gap: 8px;
	}

	.forum-message-avatar {
		width: 32px;
		height: 32px;
		font-size: 12px;
	}

	.forum-message.is-reply .forum-message-avatar {
		width: 24px;
		height: 24px;
		font-size: 10px;
	}

	.forum-message-content {
		font-size: 13.5px;
		line-height: 1.5;
	}

	.forum-message.is-reply .forum-message-content {
		font-size: 13px;
	}

	/* Action row wraps when Like + Reply + Edit + Delete don't fit. */
	.forum-message-actions {
		flex-wrap: wrap;
		gap: 10px;
		row-gap: 6px;
	}

	/* Reply / edit composers' send button gets a usable tap target. */
	.forum-reply-bar .forum-reply-send,
	.forum-edit-bar .forum-reply-send {
		padding: 8px 18px;
		font-size: 13px;
	}
}

@media (max-width: 380px) {

	/* On the smallest phones (iPhone SE, narrow Android), the cumulative
	   margin from 2 levels of replies + their avatars is still too much.
	   Drop the indent further and shrink the rule to 1px. */
	.forum-replies {
		margin-left: 12px;
		padding-left: 6px;
		border-left-width: 1px;
	}

	.forum-message-handle {
		display: none;
	}
}</style>
